<script setup lang="ts">
import type { EditorialFashionShowSliderProps } from './EditorialFashionShowSlider.props'
import type { EmblaOptionsType, EmblaCarouselType } from 'embla-carousel'
const { ts } = useI18n()

defineProps<EditorialFashionShowSliderProps>()

const emit = defineEmits<{
  'clicked-media': [index: number]
}>()

const options: EmblaOptionsType = {
  loop: true,
  skipSnaps: true,
}
const emblaApi = ref<EmblaCarouselType | null>(null)
const canScrollPrev = ref(false)
const canScrollNext = ref(false)

const onInit = (embla: Ref<EmblaCarouselType | undefined>) => {
  if (embla.value) {
    emblaApi.value = embla.value
    updateButtonStates()
  }
}

const updateButtonStates = () => {
  if (!emblaApi.value) return

  canScrollPrev.value = emblaApi.value.canScrollPrev()
  canScrollNext.value = emblaApi.value.canScrollNext()
}

const scrollPrev = () => {
  if (!emblaApi.value) return
  emblaApi.value.scrollPrev()
  updateButtonStates()
}

const scrollNext = () => {
  if (!emblaApi.value) return
  emblaApi.value.scrollNext()
  updateButtonStates()
}

const activeNavigationSide = ref<null | 'left' | 'right'>(null)

const detectHoverSide = (e: MouseEvent) => {
  activeNavigationSide.value =
    e.pageX < window.innerWidth / 2 ? 'left' : 'right'
}
const isHovering = ref(false)

watchEffect(() => {
  if (!emblaApi.value) return

  emblaApi.value.on('select', updateButtonStates)
})

onBeforeUnmount(() => {
  if (!emblaApi.value) return

  emblaApi.value.off('select', updateButtonStates)
})
</script>

<template>
  <div
    class="slider-container editorial-fashion-show-slider relative"
    @mousemove="(e: any) => detectHoverSide(e as MouseEvent)"
    @mouseleave="activeNavigationSide = null"
  >
    <!-- Navigation back -->
    <div
      class="bg-background-secondary absolute bottom-0 left-0 top-0 z-50 hidden w-8 opacity-0 transition-opacity duration-500 lg:block lg:w-10 xl:w-16"
      :class="activeNavigationSide === 'left' && 'opacity-100'"
      :aria-label="$ts('aria-labels.prevSlide')"
      @click="scrollPrev"
    >
      <div class="arrow arrow--left"></div>
    </div>

    <!-- Swiper component -->
    <UISliderBase :options="options" @init="onInit">
      <div
        v-for="(content, contentIndex) in cards"
        :key="`media-${contentIndex}`"
        class="swiper-slide swiper-slide-dimension flex w-2/3 shrink-0 flex-col gap-6 md:w-2/5 lg:gap-8"
      >
        <button
          aria-haspopup="dialog"
          :aria-label="ts('aria-labels.fashionShow')"
          class="custom-zoom-cursor-add"
          @click="emit('clicked-media', contentIndex)"
        >
          <UIMedia
            v-bind="content.media"
            :aspect-ratio="content.aspectRatio ?? '4/5'"
            class="object-cover"
          />
        </button>

        <div v-if="content.link && content.link[0]" class="flex">
          <UILink
            v-bind="content.link[0]"
            class="text-text-primary !text-book-5"
            underline-effect="appear"
          />
        </div>
        <div v-else class="flex">
          <span class="text-text-primary text-book-5">
            {{ String(contentIndex + 1).padStart(2, '0') }}
          </span>
        </div>
      </div>
    </UISliderBase>

    <!-- Navigation next -->
    <button
      class="bg-background-secondary absolute bottom-0 right-0 top-0 z-50 hidden w-8 opacity-0 transition-opacity duration-500 lg:block lg:w-10 xl:w-16"
      :class="activeNavigationSide === 'right' && 'opacity-100'"
      :aria-label="$ts('aria-labels.nextSlide')"
      @click="scrollNext"
    >
      <div class="arrow arrow--right"></div>
    </button>
  </div>
</template>

<style scoped lang="scss">
.swiper-button-next-unique::after,
.swiper-button-prev-unique::after,
.swiper-button-next-unique,
.swiper-button-prev-unique {
  --swiper-navigation-size: 14px;
  --swiper-navigation-sides-offset: 30px;
  --swiper-navigation-top-offset: 45%;
  @apply text-text-primary;
}

.arrow {
  width: 10px;
  height: 10px;
  border-top: 1px solid var(--colorTextPrimary);
  border-right: 1px solid var(--colorTextPrimary);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-135deg);

  &--right {
    transform: translate(-50%, -50%) rotate(45deg);
  }
}

.swiper-slide-dimension {
  @screen md {
    width: 40%;
  }

  @screen lg {
    width: 31%;
  }
}

.custom-zoom-cursor-add {
  cursor:
    url('@design-system/icons/AddWBkg.svg') 24 24,
    auto;
}
</style>
